import { Button } from "@/components/flexkit/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { FC } from "react";
import { useState } from "react";

type OverrideConversationUrlModalProps = {
    agentExecution: PlainAgentExecution;
    open: boolean;
    onClose: () => void;
};

export const OverrideConversationUrlModal: FC<OverrideConversationUrlModalProps> = ({
    agentExecution,
    open,
    onClose,
}) => {
    const [url, setUrl] = useState(agentExecution.status?.conversationUrl || "");

    const handleOverride = () => {
        // Store the override URL in localStorage for this conversation
        if (url.trim()) {
            localStorage.setItem(`conversation-url-override-${agentExecution.id}`, url.trim());
        } else {
            localStorage.removeItem(`conversation-url-override-${agentExecution.id}`);
        }

        // Trigger a page reload to apply the override
        window.location.reload();
        onClose();
    };

    const handleReset = () => {
        localStorage.removeItem(`conversation-url-override-${agentExecution.id}`);
        setUrl(agentExecution.status?.conversationUrl || "");
        window.location.reload();
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.OverrideConversationUrlModal}>
                <DialogHeader>
                    <DialogTitle>Override Conversation URL (Development)</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="conversation-url">Conversation URL</Label>
                        <Input
                            id="conversation-url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="Enter conversation URL..."
                            className="w-full"
                        />
                    </div>
                    <div className="text-sm text-content-secondary">
                        This will override the conversation URL for this conversation in the frontend only. The page
                        will reload to apply the change.
                    </div>
                    <div className="flex justify-end space-x-2">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-override-conversation-url-modal"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleReset}
                            data-tracking-id="reset-to-original-override-conversation-url-modal"
                        >
                            Reset to Original
                        </Button>
                        <Button
                            onClick={handleOverride}
                            data-tracking-id="apply-override-override-conversation-url-modal"
                        >
                            Apply Override
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
